import React, { useMemo } from "react";
import { Col, Label, Row } from "reactstrap";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import { Divider, Flex, Space } from "antd";
import { useParams } from "react-router";
import { useGetHouseContractsQuery } from "../../../services/makkahGroups";
import { InitialGroupForm } from "../../BasicData/InitialGroup/InitialGroupForm";
import { AddressCapacityDisplay } from "../../../CommonElements/AddressCapacityDisplay";
import { AddressDate } from "../../../Component/contracts/AddressDate";
import {
  useCreateArrivalGroupDetailMutation,
  useDeleteArrivalGroupDetailMutation,
  useUpdateArrivalGroupDetailMutation,
} from "../../../services/groups";
import { useCampaignDetailsForm } from "@/Pages/Campaigns/hooks/useCampaignDetailsForm";
import { CampaignDetailsForm } from "@/Pages/Campaigns";
import { useCampaignDetailsTable } from "@/Pages/Campaigns/hooks/useCampaignDetailsTable";
import TableC from "@/CommonElements/TableC";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { LabelValue } from "@/Component/Value";
import { useSystem } from "@/utils/hooks/useLocation";

export const MakkahGroupForm = ({
  groupDetails = [],
  initialGroupData = {},
}) => {
  // =============
  // Hooks
  // =============
  const { mode } = useParams();
  const { t } = useTranslation();
  const { confirm, ModalComponent } = useConfirm();
  const { getActions, columns } = useCampaignDetailsTable();
  const { open, handleClose, handleOpen, getSelectedSegment } =
    useCampaignDetailsForm();
  const canAmend = mode === "edit" || mode === "create";
const { currentSystem } = useSystem();
  // =============
  // Queries
  // =============
  const {
    data: contractsResponse,
    isLoading: isLoadingContracts,
    isFetching: isFetchingContracts,
  } = useGetHouseContractsQuery();

  const { mutate: createGroupDetail, isPending: isCreatingGroupDetail } =
    useCreateArrivalGroupDetailMutation();
  const { mutate: updateGroupDetail, isPending: isUpdatingGroupDetail } =
    useUpdateArrivalGroupDetailMutation();
  const { mutate: deleteGroupDetail } = useDeleteArrivalGroupDetailMutation();

  const isPending = isCreatingGroupDetail || isUpdatingGroupDetail;

  const contracts = useMemo(
    () => contractsResponse?.data?.data || [],
    [contractsResponse]
  );
  const isContractsLoading = isLoadingContracts || isFetchingContracts;
  const totalCapacity = (groupDetails || []).reduce(
    (sum, d) => sum + (Number(d.capacity) || 0),
    0
  );

  // Get addresses/contracts from selected details
  const selectedContracts = useMemo(() => {
    if (
      !groupDetails ||
      groupDetails.length === 0 ||
      !contracts ||
      contracts.length === 0
    ) {
      return [];
    }

    const contractMap = new Map();
    groupDetails.forEach((detail) => {
      const contractId = detail.contract_number || detail.contract_id;
      if (contractId) {
        const contract = contracts.find(
          (c) => String(c.id) === String(contractId)
        );
        if (contract && !contractMap.has(contract.id)) {
          contractMap.set(contract.id, contract);
        }
      }
    });

    return Array.from(contractMap.values());
  }, [groupDetails, contracts]);

  // =============
  // functions
  // =============
  const handleDelete = (id) => {
    confirm({
      title: t("CONFIRM"),
      content: (
        <Trans
          i18nKey="CONFIRM_DELETE_FLIGHT"
          components={{ danger: <span className="text-danger" /> }}
        />
      ),
      onOk: () => {
        deleteGroupDetail({ id });
      },
      onCancel: () => { },
    });
  };

  return (
    <Row>
      <InitialGroupForm
        ref={{ current: null }}
        initialData={initialGroupData}
        canAmend={false}
      />

      {/* Address and Capacity Display */}
      <Row className="mb-3">


        <AddressCapacityDisplay
          items={selectedContracts}
          isLoading={isContractsLoading}
          totalCapacity={totalCapacity}
          capacityLabel={t("TOTAL_PILGRIMS")}
          addressLabel={t("ADDRESS_LABEL")}
          emptyMessage={t("NO_ADDRESSES_FOUND")}
          pilgrimCountLabel={t("NUMBER_OF_ACCOMMODATED")}
          pilgrimCount={initialGroupData?.number_of_accommodated || 0}
          primaryItemFormatter={(contract) => {
            const { address, id } = contract;
            return [id, address].join(" - ");
          }}
          renderItem={(contract) => (
            <AddressDate contract={contract} key={contract?.id} />
          )}
        />
      </Row>
      {/* Group Details Table */}
      <Col md={12}>
        <Divider />
        <Label className="fw-bold mb-3" htmlFor="group_details">
          {t("GROUP_DETAILS_SECTION_TITLE", {
            defaultValue: "تفاصيل المجموعة",
          })}
        </Label>

        <TableC
          data={groupDetails}
          columns={columns.filter((column) =>
            canAmend ? true : column.key !== "actions"
          )}
          actions={
            canAmend
              ? getActions({
                edit: (rowData) => ({
                  onClick: () => handleOpen(rowData.id),
                }),
                delete: (rowData) => ({
                  onClick: () => handleDelete(rowData.id),
                }),
              })
              : undefined
          }
          columnsFilter={false}
        />
      </Col>
      {canAmend && (
        <Col
          md={12}
          className="d-flex justify-content-center align-items-center"
        >
          <Button className="btn-text" onClick={() => handleOpen()}>
            <Space>
              <i className="icon-plus"></i>
              <span>{t("GROUPS_FORM_ADD_GROUP_DETAILS")}</span>
            </Space>
          </Button>
        </Col>
      )}
      {/* Group Details Modal - Only for Create/Edit Mode */}
      {canAmend && (
        <CampaignDetailsForm
          extraData={[
            {
              labelProps: {
                children: t("SERVICE_CENTERS"),
                className: "required",
              },
              controllerProps: {
                name: "service_center_id",
                rules: {
                  required: t("FIELD_REQUIRED"),
                },
                render: ({ field, fieldState: { errors } }) => (
                  <SelectCachedOptions
                    allowClear
                    {...field}
                    showSearch
                    filter={(serviceCenter) => currentSystem.includes(serviceCenter.system)}
                    status={errors?.service_center_id ? "error" : undefined}
                    placeholder={
                      t("SELECT_SERVICE_CENTER") || "Select service center"
                    }
                    id="service_center_id"
                    optionKey="service_centers"
                    options={(service_center) => ({
                      value: service_center.id,
                      label: <LabelValue.Value value={service_center.name} />,
                    })}
                  />
                ),
              },
            },
          ]}
          mutation={{
            create: createGroupDetail,
            update: updateGroupDetail,
            isPending: isPending,
          }}
          totalCapacity={totalCapacity}
          // hook values
          initialData={initialGroupData}
          isOpen={open}
          onClose={handleClose}
          selectedSegment={getSelectedSegment(groupDetails) ?? {}}
          isLoading={isContractsLoading}
        />
      )}
      {<ModalComponent />}
    </Row>
  );
};
